import { Radio } from '@ksyos/design-system';
import type { BariPostNurseSpecialistFollowUpData } from '@common/validation/customControls/bariPost/bariPostNurseSpecialistFollowUp/types';
import { FollowUpStatusOpties, TherapeutischeMedicamenteuzeVeranderingOpties } from '@common/customControls/bariatrics';
import { useProcessActivityEditContext } from '@frontend/utils';
import {
    BooleanRadioGroupFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';

const therapeutischeMedicamenteuzeVeranderingOpties = [
    TherapeutischeMedicamenteuzeVeranderingOpties.gestopt,
    TherapeutischeMedicamenteuzeVeranderingOpties.verhoging,
    TherapeutischeMedicamenteuzeVeranderingOpties.verlaging,
    TherapeutischeMedicamenteuzeVeranderingOpties.geen_verandering,
];

export const followUpStatusOpties = [
    FollowUpStatusOpties.genezen,
    FollowUpStatusOpties.beter,
    FollowUpStatusOpties.gelijk,
    FollowUpStatusOpties.slechter,
    FollowUpStatusOpties.de_novo,
    FollowUpStatusOpties.niet_van_toepassing,
];
export function BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit(
    props: Readonly<{
        mainFieldKey: keyof BariPostNurseSpecialistFollowUpData;
        mainLabel: string;
        toelichtingFieldKey: keyof BariPostNurseSpecialistFollowUpData;
        veranderingenFieldKey: keyof BariPostNurseSpecialistFollowUpData;
        veranderingenType: 'medicamenteuze' | 'therapeutische';
        version: number;
    }>
): JSX.Element {
    const { veranderingenFieldKey, veranderingenType, toelichtingFieldKey, mainFieldKey, mainLabel, version } = props;
    const veranderingenLabel = `${veranderingenType.charAt(0).toUpperCase()}${veranderingenType.slice(
        1
    )} veranderingen`;
    const toelichtingLabel = `Toelichting ${veranderingenType} veranderingen`;
    const { data, onChange } = useProcessActivityEditContext<BariPostNurseSpecialistFollowUpData>();

    return (
        <>
            <RadioGroupFormGroup
                fieldKey={mainFieldKey}
                isRequired={true}
                label={mainLabel}
                onChange={handleChangeMain}
            >
                {followUpStatusOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>

            {data[mainFieldKey] && data[mainFieldKey] !== 'Niet van toepassing' && (
                <>
                    {version === 1 && (
                        <BooleanRadioGroupFormGroup
                            fieldKey={veranderingenFieldKey}
                            isRequired={true}
                            label={veranderingenLabel}
                            onChange={handleChangeVeranderingen}
                            type="radio"
                        />
                    )}
                    {version === 2 && (
                        <RadioGroupFormGroup
                            fieldKey={veranderingenFieldKey}
                            isRequired={true}
                            label={veranderingenLabel}
                            onChange={handleChangeVeranderingen}
                        >
                            {therapeutischeMedicamenteuzeVeranderingOpties.map((value) => (
                                <Radio
                                    key={value}
                                    label={value}
                                    value={value}
                                />
                            ))}
                        </RadioGroupFormGroup>
                    )}
                    {data[veranderingenFieldKey] && data[veranderingenFieldKey] !== 'Geen verandering' && (
                        <TextAreaFormGroup
                            fieldKey={toelichtingFieldKey}
                            isRequired={true}
                            label={toelichtingLabel}
                        />
                    )}
                </>
            )}
        </>
    );

    async function handleChangeMain(value: string | null): Promise<void> {
        let newValues = {
            [mainFieldKey]: value,
        };
        let validateKeys = [mainFieldKey];
        if (value === 'Niet van toepassing') {
            newValues = { ...newValues, [toelichtingFieldKey]: null, [veranderingenFieldKey]: null };
            validateKeys = [...validateKeys, toelichtingFieldKey, veranderingenFieldKey];
            if (mainFieldKey === 'diabetes_mellitus') {
                validateKeys = [...validateKeys, 'hba1c'];
            }
        }

        return onChange(newValues, validateKeys);
    }

    async function handleChangeVeranderingen(value: boolean | string | null): Promise<void> {
        let newValues = {
            [veranderingenFieldKey]: value,
        };
        let validateKeys = [veranderingenFieldKey];
        if (value === 'Geen verandering' || !value) {
            newValues = { ...newValues, [toelichtingFieldKey]: null };
            validateKeys = [...validateKeys, toelichtingFieldKey];
        }

        return onChange(newValues, validateKeys);
    }
}
